import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Eye, Edit2, Trash2 } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import AdminTable from '../../components/admin/AdminTable';
import api from '../../services/api';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import '../../styles/pages/admin/UsersPage.css';
import '../../styles/admin-shared.css';

const UsersListPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    status: '',
    addiction_type: '',
    has_subscription: ''
  });
  const [currentPage, setCurrentPage] = useState(1);

  const fetchUsers = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        limit: 20,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''))
      });

      const response = await api.get(`/admin/users?${params}`);
      
      // Verificar se a resposta tem o formato esperado
      if (response.data && response.data.data) {
        setUsers(response.data.data.users || []);
        setPagination(response.data.data.pagination || null);
      } else {
        console.error('Formato de resposta inesperado:', response.data);
        setUsers([]);
        setPagination(null);
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      if (error.response?.status === 401) {
        alert('Sessão expirada. Faça login novamente.');
        navigate('/admin/login');
      }
      setUsers([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, [filters, navigate]);

  useEffect(() => {
    fetchUsers(currentPage);
  }, [currentPage, fetchUsers]);

  const handlePageChange = useCallback((newPage) => {
    if (newPage !== currentPage) {
      setCurrentPage(newPage);
    }
  }, [currentPage]);

  const columns = [
    {
      key: 'name',
      label: 'Nome',
      render: (value, row) => (
        <div className="user-cell">
          <div className="user-avatar">{value?.charAt(0)}</div>
          <div>
            <div className="user-name">{value}</div>
            <div className="user-email">{row.email}</div>
          </div>
        </div>
      )
    },
    {
      key: 'role',
      label: 'Função',
      render: (value) => (
        <span className={`badge badge-${value.toLowerCase()}`}>
          {value}
        </span>
      )
    },
    {
      key: 'addiction_type',
      label: 'Tipo de Vício'
    },
    {
      key: 'subscription',
      label: 'Assinatura',
      render: (value) => value ? (
        <span className="badge badge-success">Ativa</span>
      ) : (
        <span className="badge badge-default">Nenhuma</span>
      )
    },
    {
      key: 'is_active',
      label: 'Status',
      render: (value) => value ? (
        <span className="status-badge active">Ativo</span>
      ) : (
        <span className="status-badge inactive">Inativo</span>
      )
    },
    {
      key: 'created_at',
      label: 'Cadastro',
      render: (value) => format(new Date(value), 'dd/MM/yyyy', { locale: ptBR })
    }
  ];

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleView = (user) => {
    navigate(`/admin/users/${user.id}`);
  };

  const handleEdit = (user) => {
    navigate(`/admin/users/${user.id}/edit`);
  };

  const handleDelete = async (user) => {
    if (window.confirm(`Deseja realmente excluir o usuário ${user.name}?`)) {
      try {
        await api.delete(`/admin/users/${user.id}`);
        fetchUsers(currentPage);
      } catch (error) {
        console.error('Erro ao excluir usuário:', error);
        alert('Erro ao excluir usuário');
      }
    }
  };

  return (
    <AdminLayout>
      <div className="users-page">
        <div className="page-header">
          <div>
            <h1>Gerenciamento de Usuários</h1>
            <p>Gerencie todos os usuários da plataforma</p>
          </div>
          <button 
            className="btn btn-primary btn-with-icon"
            onClick={() => navigate('/admin/users/new')}
          >
            <Plus size={18} /> Novo Usuário
          </button>
        </div>

        <div className="filters-section">
          <div className="filters-grid">
            <div className="filter-group search-group">
              <Search className="search-icon" size={18} />
              <input
                type="text"
                placeholder="Buscar por nome ou email..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="filter-input with-icon"
              />
            </div>

            <div className="filter-group">
              <select
                value={filters.role}
                onChange={(e) => handleFilterChange('role', e.target.value)}
                className="filter-select"
              >
                <option value="">Todas as Funções</option>
                <option value="USER">Usuário</option>
                <option value="ADMIN">Admin</option>
                <option value="MODERATOR">Moderador</option>
              </select>
            </div>

            <div className="filter-group">
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="filter-select"
              >
                <option value="">Todos os Status</option>
                <option value="active">Ativos</option>
                <option value="inactive">Inativos</option>
              </select>
            </div>

            <div className="filter-group">
              <select
                value={filters.has_subscription}
                onChange={(e) => handleFilterChange('has_subscription', e.target.value)}
                className="filter-select"
              >
                <option value="">Todas as Assinaturas</option>
                <option value="true">Com Assinatura</option>
                <option value="false">Sem Assinatura</option>
              </select>
            </div>
          </div>
        </div>

        <AdminTable
          columns={columns}
          data={users}
          loading={loading}
          pagination={pagination}
          onPageChange={handlePageChange}
          actions={(row) => (
            <div className="table-actions-row">
              <button
                className="table-action-btn view"
                onClick={() => handleView(row)}
                title="Ver detalhes"
              >
                <Eye size={18} />
              </button>
              <button
                className="table-action-btn edit"
                onClick={() => handleEdit(row)}
                title="Editar"
              >
                <Edit2 size={18} />
              </button>
              <button
                className="table-action-btn delete"
                onClick={() => handleDelete(row)}
                title="Excluir"
              >
                <Trash2 size={18} />
              </button>
            </div>
          )}
        />
      </div>
    </AdminLayout>
  );
};

export default UsersListPage;
